/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Box, Flex, Text, Button, Badge, useToast } from '@chakra-ui/react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebounce } from 'use-debounce';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { ButtonDropdown } from '@cloudscape-design/components';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import TableComponent from '../../components/Table/Cloudscape/TableComponent';
import useLocalStorage from 'use-local-storage';
import getDepartmentLabel from '../../utils/getDepartmentLabel';
import getPositionLabel from '../../utils/getPositionLabel';
import getOfficeLabel from '../../utils/getOfficeLabel';
import useStore from '../../store';
import moment from 'moment';
import c from '../../constant';
import 'moment/locale/en-gb';
import 'moment/locale/km';
import './style.css';

const NAME_COLUMN_WIDTH = 180;

const QUERY_USERS = gql`
	query getUsers($filterQuery: JSONObject, $offset: Float, $limit: Float, $name: String) {
		getUsers(filterQuery: $filterQuery, pagerInput: { limit: $limit, offset: $offset }, name: $name) {
			users {
				id
				username
				fullName
				fullNameKhmer
				createdAt
				role
				department
				office
				position
				active
				isTwoFactorEnabled
			}
			totalPages
			totalRows
		}
	}
`;

const UPDATE_USER_MUTATION = gql`
	mutation updateUserByAdmin($id: String!, $active: Boolean) {
		updateUserByAdmin(id: $id, active: $active)
	}
`;

const CONVERT_USER_TO_EMPLOYEE = gql`
	mutation convert($id: String!) {
		convertUserToEmployeeById(id: $id)
	}
`;

const M_CREDENTIAL = gql`
	mutation ($username: String!) {
		generateUserCredential(username: $username) {
			accessToken
		}
	}
`;


const CLEAR_2FA = gql`
	mutation adminClear2fa($id: String!) {
		adminClear2fa(id:$id)
	}
`;

function UserList() {
	const history = useHistory();
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const [loadUsers, { loading, data }] = useLazyQuery(QUERY_USERS);
	const [updateUserByAdmin, { data: dataUpdate }] = useMutation(UPDATE_USER_MUTATION);
	const [convertUserToEmployee] = useMutation(CONVERT_USER_TO_EMPLOYEE);
	const [generateUserCredential] = useMutation(M_CREDENTIAL);
	const [onAdminClear2fa] = useMutation(CLEAR_2FA);
	const [tmpId, setTmpId] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [filterQuery, setFilterQuery] = useState(null);

	moment.locale(currentLang === 'kh' ? 'km' : 'en-gb');

	function loadUserWithVar() {
		loadUsers({
			variables: {
				offset: currentPage ? (currentPage - 1) * pageSize : 0,
				limit: pageSize,
				name: deboundSearchText,
				filterQuery,
			},
		});
	}

	useEffect(() => {
		if (dataUpdate?.updateUserByAdmin) {
			loadUserWithVar();
			if (tmpId) {
				convertUserToEmployee({
					variables: { id: tmpId },
				});
			}
			setTmpId(null);
			toast({
				title: t('User update successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
		}
	}, [dataUpdate]);

	function updateUser({ id, active }) {
		setTmpId(id);
		updateUserByAdmin({
			variables: {
				id,
				active: !active,
			},
		});
	}

	function onClear2fa(id){
		onAdminClear2fa({variables:{id}})
	}

	function actionMenu({ id, active, username }) {
		const items = user?.role === 'admin' ? [{ id: 'copyCredential', text: t('Copy Credential') }] : [];

		return (
			<ButtonDropdown
				className="action-button-table"
				variant="inline-icon"
				ariaLabel={`${id} actions`}
				expandToViewport={true}
				items={[
					{ id: 'toggleActiveInactive', text: <p style={{ color: active ? 'red' : '#27ae60' }}>{active ? t('Deactivate') : t('Activate')}</p> },
					{ id: 'transferProposal', text: t('Transfer Proposal') },
					{ id: 'transferEwsReport', text: t('Transfer Early Warning Report') },
					{ id: 'clear2fa', text: t('Clear 2FA') },
					...items,
				]}
				onItemClick={(e) => {
					switch (e?.detail?.id) {
						case 'toggleActiveInactive':
							updateUser({ id, active });
							break;
						case 'transferProposal':
							history.push('/users/transfer-proposal?id=' + id);
							break;
						case 'transferEwsReport':
							history.push('/users/transfer-ews-report?id=' + id);
							break;
						case 'copyCredential':
							generateUserCredential({ variables: { username } })
								.then(({ data }) => {
									navigator?.clipboard?.writeText(data?.generateUserCredential?.accessToken);
									toast({
										title: t('Credential copied to clipboard'),
										status: 'success',
										isClosable: true,
										position: 'bottom-right',
									});
								})
								.catch((error) => {
									toast({
										title: t(error?.message ? error?.message : 'something went wrong'),
										status: 'error',
										isClosable: true,
										position: 'bottom-right',
									});
								});
							break;
						case 'clear2fa':
							onClear2fa(id)
							break;
						default:
							break;
					}
				}}
			/>
		);
	}

	// Table config
	const [totalPage, setTotalPage] = useState(0);
	const tableName = 'listuser';
	const rawColumns = [
		{
			id: 'username',
			label: t('Username'),
			alwayVisible: true,
			header: <Flex style={{ paddingLeft: 20, paddingRight: 20 }}>{t('Username')}</Flex>,
			cell: (item) => (
				<Link to={`/users/update?id=${item?.id}`}>
					<Flex pl="20px" pr="20px" cursor="pointer">
						<Text fontWeight={600} color="#0972d3" textDecoration="underline">
							{item.username}
						</Text>
					</Flex>
				</Link>
			),
			isRowHeader: true,
			width: 160,
		},
		{
			id: 'fullNameKhmer',
			label: t('Full Name Khmer'),
			header: t('Full Name Khmer'),
			cell: (item) => item.fullNameKhmer || '-',
			width: NAME_COLUMN_WIDTH,
		},
		{
			id: 'fullName',
			label: t('Full Name'),
			header: t('Full Name'),
			cell: (item) => item.fullName || '-',
			width: NAME_COLUMN_WIDTH,
		},
		{
			id: 'position',
			label: t('Position'),
			header: t('Position'),
			cell: (item) => (item?.position ? getPositionLabel(item?.position) : '-'),
			width: NAME_COLUMN_WIDTH,
		},
		{
			id: 'department',
			label: t('Department'),
			header: t('Department'),
			cell: (item) => (item?.department ? t(getDepartmentLabel(item.department)) : '-'),
			width: 350,
		},
		{
			id: 'office',
			label: t('Office'),
			header: t('Office'),
			cell: (item) => getOfficeLabel(item.office, currentLang) || '-',
			width: 350,
		},
		{
			id: 'createdAt',
			label: t('Created At'),
			header: t('Created At'),
			cell: (item) => (item.createdAt ? moment(item.createdAt).format('LLLL') : ''),
			width: 230,
		},
		{
			id: 'status',
			label: t('Status'),
			header: t('Status'),
			cell: (item) => (item.active ? <Badge colorScheme="green">{t('ACTIVE')}</Badge> : <Badge colorScheme="red">{t('INACTIVE')}</Badge>),
		},
		{
			id: 'isTwoFactorEnabled',
			label: t('2FA'),
			header: t('2FA'),
			cell: (item) => (item.isTwoFactorEnabled ? <Badge colorScheme="green">{t('✓')}</Badge> : <Badge colorScheme="red">{t('✕')}</Badge>),
		},
		{
			id: 'actions',
			label: t('Actions'),
			header: t('Actions'),
			cell: (item) => actionMenu({ id: item?.id, active: item?.active, username: item?.username }),
		},
	];

	const [preferences, setPreferences] = useLocalStorage(`${tableName}-table-Preferences`, {
		pageSize: 10,
		contentDisplay: rawColumns.map((column) => ({ id: column.id, visible: true })),
		wrapLines: false,
		stripedRows: false,
		contentDensity: 'comfortable',
		stickyColumns: { first: 1, last: 1 },
	});

	const { pageSize } = preferences;

	function getHeader() {
		return (
			<Flex className="table-header" direction={'row'} alignItems={'center'}>
				<Flex flexGrow={1} className="title">
					<Text fontSize="xl" fontWeight={600} mt="10px" mb="10px">
						{t('Users')}
					</Text>
				</Flex>
				<Flex direction={'row'}>
					{
						<Link to={`/users/create`}>
							<Button
								ml="10px"
								onClick={() => null}
								leftIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 16 }} />}
								colorScheme={`${false ? 'yellow' : 'gray'}`}
								variant="solid"
								size="sm"
								borderRadius={c.borderRadius}
								border="1px solid #bdc3c7}"
							>
								{t('Add')}
							</Button>
						</Link>
					}
				</Flex>
			</Flex>
		);
	}
	// end table config

	useEffect(() => {
		loadUserWithVar();
		window.scrollTo(0, 0);
	}, []);

	function onTablePageChange(e) {
		setCurrentPage(e?.detail?.currentPageIndex);
	}

	const [searchText, setSearchText] = useState('');
	const [deboundSearchText] = useDebounce(searchText, 1000);
	function onSearchTextChange(e) {
		setSearchText(e?.detail?.filteringText);
	}

	useEffect(() => {
		setCurrentPage(1);
	}, [deboundSearchText]);

	useEffect(() => {
		loadUserWithVar();
	}, [currentPage, deboundSearchText, pageSize, filterQuery]);

	useEffect(() => {
		if (data?.getUsers?.totalPages) {
			setTotalPage(data?.getUsers?.totalPages || 1);
		}
	}, [data]);

	return (
		<>
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: 'USERS',
							path: '#',
						},
					]}
				/>
				<Box pb="32px" w="100%" maxW={'8xl'} mt="5">
					<TableComponent
						tableName={tableName}
						rawColumns={rawColumns}
						currentPage={currentPage}
						totalPage={totalPage}
						loading={loading}
						items={data?.getUsers?.users || []}
						preferences={preferences}
						setPreferences={setPreferences}
						onTablePageChange={onTablePageChange}
						onSearchTextChange={onSearchTextChange}
						searchText={searchText}
						totalRows={data?.getUsers?.totalRows || ''}
						header={getHeader()}
						onQueryChange={(e) => {
							setCurrentPage(1);
							setFilterQuery(e);
						}}
						filteringOptions={[
							{ propertyKey: 'active', value: true, label: t('ACTIVE') },
							{ propertyKey: 'active', value: false, label: t('INACTIVE') },
						]}
						filteringProperties={[
							{
								key: 'username',
								operators: [':'],
								propertyLabel: t('Username'),
							},
							{
								key: 'fullNameKhmer',
								operators: [':'],
								propertyLabel: t('Full Name Khmer'),
							},
							{
								key: 'fullName',
								operators: [':'],
								propertyLabel: t('Full Name'),
							},
							{
								key: 'active',
								operators: [':'],
								propertyLabel: t('Status'),
							},
						]}
					/>
				</Box>
			</Container>
		</>
	);
}

export default UserList;
